import { brandAssets } from '@/assets/brand';
import { useI18n } from '@/i18n/context';
import styles from './Logo.module.css';

export type LogoTone = 'onLight' | 'onDark';

/**
 * Brand lockup composed ONLY from the supplied artwork (no redrawn or
 * typeset logo): Johnson Window Films lockup + Arabic wordmark.
 * The side-by-side arrangement follows the guide's combined lockup
 * (identity guide p.7–8). Layout REQUIRES BUSINESS CONFIRMATION once the
 * vector master and a tagline-free combined lockup are supplied.
 */
export function Logo({
  tone = 'onLight',
  variant = 'full',
  height = 36,
}: {
  tone?: LogoTone;
  variant?: 'full' | 'wordmark' | 'mark';
  height?: number;
}) {
  const { t } = useI18n();
  const label = `${t.common.brandNameAr} — ${t.common.brandName} · ${t.common.partnerBrand}`;
  const lockup = brandAssets.johnsonLockup[tone];
  const wordmark = brandAssets.wordmarkAr[tone];
  const mark = brandAssets.johnsonMark[tone];

  if (variant === 'mark') {
    return <img src={mark.src} width={Math.round((mark.width / mark.height) * height)} height={height} alt={label} className={styles.img} />;
  }

  if (variant === 'wordmark') {
    const h = Math.round(height * 0.62);
    return <img src={wordmark.src} width={Math.round((wordmark.width / wordmark.height) * h)} height={h} alt={label} className={styles.img} />;
  }

  // The Arabic wordmark is visually shorter than the two-line Johnson lockup.
  const wordmarkHeight = Math.round(height * 0.56);
  return (
    <span className={[styles.lockup, tone === 'onDark' ? styles.dark : ''].join(' ')} role="img" aria-label={label} dir="ltr">
      <img
        src={lockup.src}
        width={Math.round((lockup.width / lockup.height) * height)}
        height={height}
        alt=""
        className={styles.img}
      />
      <span className={styles.divider} aria-hidden="true" />
      <img
        src={wordmark.src}
        width={Math.round((wordmark.width / wordmark.height) * wordmarkHeight)}
        height={wordmarkHeight}
        alt=""
        className={styles.img}
      />
    </span>
  );
}
